export type DailyLoginRewardTier = "normal" | "bigger" | "substantial";

export interface DailyLoginRewardDef {
  day: number;
  coins: number;
  gems: number;
  tier: DailyLoginRewardTier;
}

export interface DailyLoginBonusStatus {
  currentDay: number;
  claimableDay: number | null;
  canClaimToday: boolean;
  lastClaimOn: string | null;
  todayUtc: string;
}

export interface DailyLoginClaimResult {
  balance: {
    coins: number;
    gems: number;
  };
  status: DailyLoginBonusStatus;
  reward: DailyLoginRewardDef;
}

export const DAILY_REWARDS_CYCLE = 7;

export const DAILY_LOGIN_REWARDS: readonly DailyLoginRewardDef[] = [
  { day: 1, coins: 25, gems: 1, tier: "normal" },
  { day: 2, coins: 25, gems: 1, tier: "normal" },
  { day: 3, coins: 75, gems: 2, tier: "bigger" },
  { day: 4, coins: 30, gems: 1, tier: "normal" },
  { day: 5, coins: 90, gems: 2, tier: "bigger" },
  { day: 6, coins: 30, gems: 1, tier: "normal" },
  { day: 7, coins: 200, gems: 4, tier: "substantial" },
] as const;

const rewardsByDay = new Map<number, DailyLoginRewardDef>(
  DAILY_LOGIN_REWARDS.map((reward) => [reward.day, reward])
);

/** Get reward for a cycle day (1-7). Wraps any currentDay to the 7-day cycle. */
export function getDailyLoginReward(day: number): DailyLoginRewardDef | null {
  const cycleDay = wrapToCycleDay(day);
  return rewardsByDay.get(cycleDay) ?? null;
}

/** Convert any currentDay (1+) to a cycle day (1-7). */
export function wrapToCycleDay(day: number): number {
  if (day <= 0) return 1;
  return ((day - 1) % DAILY_REWARDS_CYCLE) + 1;
}

export function currentUtcDateString(nowMs = Date.now()): string {
  return new Date(nowMs).toISOString().slice(0, 10);
}

/** Minimum milliseconds between daily claims (20 hours) to prevent timezone exploits. */
export const MIN_CLAIM_GAP_MS = 20 * 60 * 60 * 1000;
